/**
 * Admin API Service
 * 
 * Real admin API service that connects to the platform backend
 * Provides admin-specific functionality for user management, transactions, statistics, etc.
 */

// TypeScript interfaces for admin-specific data structures
export interface AdminUser {
  _id: string;
  username: string;
  emails: Array<{
    address: string;
    verified: boolean;
  }>;
  telephone?: {
    number: string;
    verified: boolean;
  };
  profile?: {
    firstName?: string;
    lastName?: string;
  };
  roles: string[];
  balances: Record<string, number>;
  currency: string;
  hasPin: boolean;
  pinEnabled: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
  isVerified: boolean;
  active: boolean;
  createdAt: string;
  lastLoginAt?: string;
  business?: any[];
  defaultPosition?: {
    lat: number;
    lng: number;
    rad: number;
  };
  // User flags - boolean properties that control user behavior and access
  getSectionDisabled?: boolean;
  isGuest?: boolean;
  // Payment methods information for admin view
  paymentMethods?: {
    stripe?: {
      customer_id?: string;
      live_customer_id?: string;
    };
    paypal?: {
      email?: string;
    };
    plaid?: {
      accounts?: Array<{
        account_id: string;
        name: string;
        type: string;
      }>;
    };
  };
}

export interface AdminTransaction {
  _id: string;
  type: 'cash' | 'invoice' | 'balance_up' | 'balance_down' | 'referral';
  status: 'pending' | 'completed' | 'expired' | 'failed';
  amount?: number;
  currency?: string;
  description?: string;
  message?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt?: string;
  completedAt?: string;
  expiresAt?: string;
  data?: {
    amount?: number;
    currency?: string;
    message?: string;
    validity?: {
      start: Date;
      end: Date;
    };
  };
  reason?: string | null;
  source?: string;
  originator?: string;
  recipient?: string;
  key?: string;
  // Additional fields returned by backend
  userID?: string;
  recipientAmount?: number;
  code?: string;
  details?: string;
  prepaidCard?: {
    tokenId: string;
    uniqueUserId: string;
    cardNumber?: string;
    cardExpMth?: number;
    cardExpYear?: number;
    cardCvv?: string;
    cardHolderName?: string;
    status: string;
    cardCurrency?: string;
    amountLoaded?: number;
    createdAt?: string;
    convertedAt?: string;
  };
}

export interface AdminClient {
  _id: string;
  name: string;
  slug: string;
  description?: string;
  apiKey: string;
  domain: string;
  allowedOrigins: string[];
  stripe: {
    test: {
      publishable_key: string;
      secret_key: string;
      webhook_secret: string;
    };
    live: {
      publishable_key: string;
      secret_key: string;
      webhook_secret: string;
    };
    account_id?: string;
    country: string;
    currency: string;
  };
  features: {
    payments: boolean;
    subscriptions: boolean;
    marketplace: boolean;
    webhooks: boolean;
  };
  rateLimits: {
    requestsPerMinute: number;
    requestsPerHour: number;
    requestsPerDay: number;
  };
  status: 'active' | 'suspended' | 'pending';
  environment: 'development' | 'staging' | 'production';
  createdAt: string;
  updatedAt: string;
  lastAccessAt?: string;
  contact: {
    name: string;
    email: string;
    phone: string;
  };
  webhooks: {
    enabled: boolean;
    url: string;
    events: string[];
    secret: string;
  };
}

export interface AdminReferralCode {
  _id: string;
  code: string;
  createdBy: string;
  createdAt: string;
  usedBy?: string;
  usedAt?: string;
  amount: number;
  currency: string;
  status: 'active' | 'used' | 'expired';
  maxUses: number;
  currentUses: number;
  expiresAt?: string;
}

export interface AdminDashboardStats {
  period: string;
  dateRange: {
    start: Date;
    end: Date;
  };
  filters: {
    users: string[];
    currencies: string[] | 'all';
    includeGroupMembers: boolean;
  };
  overview: {
    totalTransactions: number;
    activeCurrencies: string[];
    totalUsers: number;
    totalVolume: Record<string, number>;
  };
  statistics: {
    created: { count: number; value: Record<string, number>; transactions: AdminTransaction[] };
    redeemed: { count: number; value: Record<string, number>; transactions: AdminTransaction[] };
    expired: { count: number; value: Record<string, number>; transactions: AdminTransaction[] };
    pending: { count: number; value: Record<string, number>; transactions: AdminTransaction[] };
  };
  byCurrency: Record<string, {
    transactions: number;
    volume: number;
    created: number;
    redeemed: number;
  }>;
  balances?: Record<string, number>;
  tokenTypes?: {
    cash: { count: number; value: Record<string, number> };
    invoice: { count: number; value: Record<string, number> };
    referral: { count: number; value: Record<string, number> };
    balance_up: { count: number; value: Record<string, number> };
    balance_down: { count: number; value: Record<string, number> };
  };
  detailedTransactions?: {
    created: AdminTransaction[];
    redeemed: AdminTransaction[];
    expired: AdminTransaction[];
    pending: AdminTransaction[];
  };
}

// Admin API Client
class AdminApiClient {
  private baseURL = '/api/v1';
  private token: string | null = null;

  constructor() {
    // Auto-load existing token from localStorage on initialization
    const existingToken = localStorage.getItem('authToken');
    if (existingToken) {
      this.token = existingToken;
    }
  }

  setToken(token: string) {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  removeToken() {
    this.token = null;
    localStorage.removeItem('authToken');
  }

  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Add user authentication token if available
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    try {
      console.log(`🔧 Admin API request to: ${url}`);
      
      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Don't automatically remove token here - let AuthContext handle it
          // This prevents silent logout when navigating between pages
          console.warn('🚨 Admin API returned 401, but not auto-removing token to prevent silent logout');
          throw new Error('Admin authentication required - please log in again');
        }
        
        if (response.status === 403) {
          throw new Error('Admin access required - insufficient permissions');
        }
        
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          const responseText = await response.text();
          if (responseText) {
            try {
              const errorData = JSON.parse(responseText);
              if (errorData?.error?.message) {
                errorMessage = errorData.error.message;
              } else if (errorData?.error && typeof errorData.error === 'string') {
                errorMessage = errorData.error;
              } else if (errorData?.message) {
                errorMessage = errorData.message;
              }
            } catch (jsonError) {
              errorMessage = responseText.length > 200 ? 
                `${response.status}: ${responseText.substring(0, 200)}...` : 
                `${response.status}: ${responseText}`;
            }
          }
        } catch (textError) {
          errorMessage = `${response.status}: ${response.statusText}`;
        }
        
        throw new Error(errorMessage);
      }

      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('text/html')) {
        throw new Error('Admin backend service is unavailable. Please check if the backend server is running.');
      }
      
      try {
        const responseData = await response.json();
        return responseData;
      } catch (jsonError) {
        throw new Error('Invalid response format from admin API.');
      }
    } catch (error) {
      console.error(`❌ Admin API request failed:`, error);
      throw error;
    }
  }

  // Dashboard Statistics - Use the existing working statistics endpoint with time filtering
  async getDashboardStats(options?: {
    users?: string[];
    userGroup?: string;
    currencies?: string[];
    dateRange?: {
      start: Date;
      end: Date;
    };
    timePeriod?: 'today' | 'yesterday' | 'this-week' | 'last-week' | 'this-month' | 'last-month' | 'this-year' | 'last-year' | 'all';
    includeBalances?: boolean;
    includeBreakdowns?: boolean;
    includeGroupMembers?: boolean;
  }): Promise<any> {
    // If time period is specified, add query parameters
    let url = '/statistics';
    const params = new URLSearchParams();
    
    // Admin panel should always show stats for ALL users
    params.append('userScope', 'all');
    
    if (options?.timePeriod) {
      params.append('timePeriod', options.timePeriod);
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    console.log(`🔧 Admin API calling: ${url} with options:`, {
      ...options,
      userScope: 'all' // Explicitly showing this is for all users
    });
    
    const response = await this.request<{ stats: any }>(url);
    return response.stats;
  }

  // User Management
  async getUsers(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    role?: string;
    status?: string;
    verified?: boolean;
    includeTestUsers?: boolean;
  }): Promise<{
    users: AdminUser[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.verified !== undefined) params.append('verified', filters.verified.toString());
    if (filters?.includeTestUsers !== undefined) params.append('includeTestUsers', filters.includeTestUsers.toString());
    
    const response = await this.request<{
      users: AdminUser[];
      total: number;
      page: number;
      totalPages: number;
    }>(`/admin/users?${params}`);
    
    return response;
  }

  async getUser(userId: string): Promise<AdminUser> {
    const response = await this.request<{ user: AdminUser }>(`/admin/users/${userId}`);
    return response.user;
  }

  async updateUser(userId: string, updates: Partial<AdminUser>): Promise<AdminUser> {
    const response = await this.request<{ user: AdminUser }>(`/admin/users/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
    return response.user;
  }

  async deleteUser(userId: string): Promise<void> {
    await this.request(`/admin/users/${userId}`, {
      method: 'DELETE',
    });
  }

  async createUser(userData: {
    username?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    roles?: string[];
    active?: boolean;
    password?: string;
  }): Promise<AdminUser> {
    const response = await this.request<{ user: AdminUser; generatedPassword?: string }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    return response.user;
  }

  async updateUserRoles(userId: string, roles: string[]): Promise<AdminUser> {
    const response = await this.request<{ user: AdminUser }>(`/admin/users/${userId}/roles`, {
      method: 'PATCH',
      body: JSON.stringify({ roles }),
    });
    return response.user;
  }

  async getUserBalance(userId: string): Promise<Record<string, number>> {
    const response = await this.request<{ balances: Record<string, number> }>(`/admin/users/${userId}/balance`);
    return response.balances;
  }

  async updateUserBalance(userId: string, currency: string, amount: number, operation: 'add' | 'subtract' | 'set'): Promise<void> {
    await this.request(`/admin/users/${userId}/balance`, {
      method: 'PATCH',
      body: JSON.stringify({ currency, amount, operation }),
    });
  }

  async updateUserFlags(userId: string, flags: Partial<{
    hasPin: boolean;
    pinEnabled: boolean;
    emailVerified: boolean;
    phoneVerified: boolean;
    isVerified: boolean;
    active: boolean;
    getSectionDisabled: boolean;
    isGuest: boolean;
  }>): Promise<AdminUser> {
    // Use the general updateUser endpoint which supports partial updates
    // This allows updating flags along with other user properties
    return this.updateUser(userId, flags);
  }

  // Transaction Management
  async getTransactions(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    status?: string;
    currency?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    transactions: AdminTransaction[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.currency) params.append('currency', filters.currency);
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    
    // Use the regular transactions endpoint which supports admin access
    // The backend will return all transactions if the user has admin role
    const response = await this.request<{
      transactions: AdminTransaction[];
      pending?: AdminTransaction[];
      completed?: AdminTransaction[];
      total: number;
      pendingCount?: number;
      completedCount?: number;
      page?: number;
      totalPages?: number;
    }>(`/transactions?${params}`);
    
    // Normalize the response to match expected format
    return {
      transactions: response.transactions || [],
      total: response.total || 0,
      page: filters?.page || 1,
      totalPages: response.totalPages || Math.ceil((response.total || 0) / (filters?.limit || 15))
    };
  }

  async getTransaction(transactionId: string): Promise<AdminTransaction> {
    // Use the regular transaction endpoint which supports admin access
    const response = await this.request<AdminTransaction>(`/transactions/${transactionId}`);
    return response;
  }

  async updateTransaction(transactionId: string, updates: Partial<AdminTransaction>): Promise<AdminTransaction> {
    const response = await this.request<{ transaction: AdminTransaction }>(`/admin/transactions/${transactionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.transaction;
  }

  async deleteTransaction(transactionId: string): Promise<void> {
    await this.request(`/admin/transactions/${transactionId}`, {
      method: 'DELETE',
    });
  }

  // Client Management
  async getClients(filters?: {
    status?: string;
    environment?: string;
  }): Promise<AdminClient[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.environment) params.append('environment', filters.environment);
    
    const response = await this.request<{ clients: AdminClient[] }>(`/admin/clients?${params}`);
    return response.clients;
  }

  async createClient(clientData: Partial<AdminClient>): Promise<{
    clientId: string;
    apiKey: string;
    apiSecret: string;
    client: AdminClient;
  }> {
    const response = await this.request<{
      clientId: string;
      apiKey: string;
      apiSecret: string;
      client: AdminClient;
    }>('/admin/clients', {
      method: 'POST',
      body: JSON.stringify(clientData),
    });
    return response;
  }

  async updateClient(clientId: string, updates: Partial<AdminClient>): Promise<AdminClient> {
    const response = await this.request<{ client: AdminClient }>(`/admin/clients/${clientId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.client;
  }

  async updateClientStripe(clientId: string, stripeConfig: any): Promise<void> {
    await this.request(`/admin/clients/${clientId}/stripe`, {
      method: 'PATCH',
      body: JSON.stringify(stripeConfig),
    });
  }

  async regenerateClientKeys(clientId: string): Promise<{
    apiKey: string;
    apiSecret: string;
  }> {
    const response = await this.request<{
      apiKey: string;
      apiSecret: string;
    }>(`/admin/clients/${clientId}/regenerate-keys`, {
      method: 'POST',
    });
    return response;
  }

  async deleteClient(clientId: string): Promise<void> {
    await this.request(`/admin/clients/${clientId}`, {
      method: 'DELETE',
    });
  }

  // Referral Management
  async getReferralCodes(filters?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    createdBy?: string;
  }): Promise<{
    referralCodes: AdminReferralCode[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.createdBy) params.append('createdBy', filters.createdBy);
    
    const response = await this.request<{
      referralCodes: AdminReferralCode[];
      total: number;
      page: number;
      totalPages: number;
    }>(`/admin/referral-codes?${params}`);
    
    return response;
  }

  async createReferralCode(referralData: {
    code: string;
    amount: number;
    currency: string;
    maxUses: number;
    expiresAt?: string;
  }): Promise<AdminReferralCode> {
    const response = await this.request<{ referralCode: AdminReferralCode }>('/admin/referral-codes', {
      method: 'POST',
      body: JSON.stringify(referralData),
    });
    return response.referralCode;
  }

  async updateReferralCode(referralId: string, updates: Partial<AdminReferralCode>): Promise<AdminReferralCode> {
    const response = await this.request<{ referralCode: AdminReferralCode }>(`/admin/referral-codes/${referralId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
    return response.referralCode;
  }

  async deleteReferralCode(referralId: string): Promise<void> {
    await this.request(`/admin/referral-codes/${referralId}`, {
      method: 'DELETE',
    });
  }

  // Export functionality
  async exportUsers(format: 'csv' | 'json', filters?: any): Promise<{ downloadUrl: string }> {
    const params = new URLSearchParams({ format });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
    }
    
    return this.request(`/admin/users/export?${params}`);
  }

  async exportTransactions(format: 'csv' | 'json', filters?: any): Promise<{ downloadUrl: string }> {
    const params = new URLSearchParams({ format });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
    }
    
    return this.request(`/admin/transactions/export?${params}`);
  }

  // System Settings
  async getSystemSettings(): Promise<any> {
    const response = await this.request<{ settings: any }>('/admin/settings');
    return response.settings;
  }

  async updateSystemSettings(settings: any): Promise<void> {
    await this.request('/admin/settings', {
      method: 'PUT',
      body: JSON.stringify(settings),
    });
  }

  // Audit Logs
  async getAuditLogs(filters?: {
    page?: number;
    limit?: number;
    action?: string;
    userId?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{
    logs: any[];
    total: number;
    page: number;
    totalPages: number;
  }> {
    const params = new URLSearchParams();
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.action) params.append('action', filters.action);
    if (filters?.userId) params.append('userId', filters.userId);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    
    const response = await this.request<{
      logs: any[];
      total: number;
      page: number;
      totalPages: number;
    }>(`/admin/audit-logs?${params}`);
    
    return response;
  }

  // System Health
  async getSystemHealth(): Promise<{
    status: 'healthy' | 'degraded' | 'unhealthy';
    services: Record<string, {
      status: 'up' | 'down' | 'degraded';
      responseTime?: number;
      lastCheck: string;
    }>;
    uptime: number;
  }> {
    const response = await this.request<{
      status: 'healthy' | 'degraded' | 'unhealthy';
      services: Record<string, {
        status: 'up' | 'down' | 'degraded';
        responseTime?: number;
        lastCheck: string;
      }>;
      uptime: number;
    }>('/admin/health');
    
    return response;
  }
}

// Create and export a singleton instance
export const adminApi = new AdminApiClient();

// Export default for easy importing
export default adminApi; 